import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useExpenses } from './ExpenseContext';
import { useChecklists } from './ChecklistContext';

const GoalContext = createContext(null);

export function GoalProvider({ children }) {
  const { isReady, isAuthenticated } = useAuth();
  const { budgets, budgetsLoaded, adoptBudget, forgetBudget } = useExpenses();
  const { checklists, checklistsLoaded, adoptChecklist, forgetChecklist } = useChecklists();
  const [goals, setGoals] = useState([]);
  const canFetch = isReady && isAuthenticated;

  useEffect(() => {
    if (!canFetch) {
      setGoals([]);
      return undefined;
    }
    let cancelled = false;
    api.get('/goals')
      .then(({ goals: fetchedGoals }) => {
        if (!cancelled) setGoals(fetchedGoals);
      })
      .catch(() => {
        if (!cancelled) setGoals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch]);

  // The server nulls a goal's linkedBudgetId/linkedChecklistId the moment that
  // budget/checklist is deleted directly from Expenses/Checklist (onDelete:
  // SetNull in the schema), but this goals list was fetched earlier and won't
  // know that happened - so its "In Savings"/"Checklist ready" badge would
  // stay stuck on until the next full reload. Clear it locally as soon as the
  // linked id disappears from either list. Guarded on the *Loaded flags so an
  // empty array on first mount (before the real data has arrived) doesn't get
  // mistaken for "everything was deleted".
  useEffect(() => {
    if (!budgetsLoaded) return;
    const budgetIds = new Set(budgets.map((budget) => budget.id));
    setGoals((current) => {
      let changed = false;
      const next = current.map((goal) => {
        if (goal.linkedBudgetId && !budgetIds.has(goal.linkedBudgetId)) {
          changed = true;
          return { ...goal, linkedBudgetId: null };
        }
        return goal;
      });
      return changed ? next : current;
    });
  }, [budgets, budgetsLoaded]);

  useEffect(() => {
    if (!checklistsLoaded) return;
    const checklistIds = new Set(checklists.map((checklist) => checklist.id));
    setGoals((current) => {
      let changed = false;
      const next = current.map((goal) => {
        if (goal.linkedChecklistId && !checklistIds.has(goal.linkedChecklistId)) {
          changed = true;
          return { ...goal, linkedChecklistId: null };
        }
        return goal;
      });
      return changed ? next : current;
    });
  }, [checklists, checklistsLoaded]);

  // A goal can be created with a checklist and/or a savings budget attached, which the
  // server makes in the same transaction - fold those into the other contexts so
  // Checklist and Expenses show them without waiting for a reload.
  const addGoal = useCallback(async (goal) => {
    const { goal: created, checklist, budget } = await api.post('/goals', goal);
    setGoals((current) => [...current, created]);
    if (checklist) adoptChecklist(checklist);
    if (budget) adoptBudget(budget);
    return created;
  }, [adoptChecklist, adoptBudget]);

  const updateGoal = useCallback(async (updatedGoal) => {
    const { goal: updated } = await api.patch(`/goals/${updatedGoal.id}`, updatedGoal);
    setGoals((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    return updated;
  }, []);

  // The server also deletes the Saving budget and checklist this goal created, and
  // reports their ids back - drop them from the other contexts so Expenses/Checklist
  // don't keep showing rows that no longer exist until the next full page load.
  const deleteGoal = useCallback(async (id) => {
    const { deletedBudgetId, deletedChecklistId } = await api.delete(`/goals/${id}`);
    setGoals((current) => current.filter((item) => item.id !== id));
    if (deletedBudgetId) forgetBudget(deletedBudgetId);
    if (deletedChecklistId) forgetChecklist(deletedChecklistId);
  }, [forgetBudget, forgetChecklist]);

  const reorderGoal = useCallback(async (id, newOrder) => {
    setGoals((current) => current.map((item) => (item.id === id ? { ...item, order: newOrder } : item))
      .sort((a, b) => a.order - b.order));
    await api.patch(`/goals/${id}`, { order: newOrder });
  }, []);

  const value = useMemo(() => ({
    goals, addGoal, updateGoal, deleteGoal, reorderGoal,
  }), [goals, addGoal, updateGoal, deleteGoal, reorderGoal]);

  return <GoalContext.Provider value={value}>{children}</GoalContext.Provider>;
}

export function useGoals() {
  return useContext(GoalContext);
}
